import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { useCurrentWorkspace } from '../useCurrentWorkspace'
import { useRestClient } from '@/contexts/QueryProvider'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { EmptyState } from '@/components/ui/EmptyState'
import { ToggleGroup, ToggleGroupItem } from '@/components/shadcn/toggle-group'
import { buttonVariants } from '@/components/shadcn/button'
import { Link } from 'react-router'
import { resolveCustomMenuIdentity } from '@/pages/Applications/AppMenuBuilderPage/utils'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

const DEFAULT_TAB_VALUE = 'default'

export function CustomMenuSection() {
  const workspace = useCurrentWorkspace()
  const { workspaceId, app_id: applicationId, app_key: appKey, custom_menu_key } = workspace

  const {
    getUiApiGetCustomMenus,
    postWorkspacesApiSetWorkspaceCustomMenuKey,
    getWorkspacesApiGetCurrentUserWorkspaces,
    getWorkspacesApiGetCurrentUserWorkspace,
    getLandingApiGetPlatformOperations,
  } = useRestClient()

  const {
    data: customMenus,
    isPending: customMenusPending,
    isError: customMenusError,
  } = getUiApiGetCustomMenus.useQuery({ path: { applicationId } }, { enabled: !!applicationId })

  const menuOptions = useMemo(() => {
    const options: { key: string; name: string }[] = []

    for (const menu of customMenus ?? []) {
      const identity = resolveCustomMenuIdentity(menu)
      if (identity) {
        options.push(identity)
      }
    }

    return options
  }, [customMenus])

  const assignedTab = custom_menu_key || DEFAULT_TAB_VALUE
  const [selectedTab, setSelectedTab] = useState(assignedTab)

  useEffect(() => {
    setSelectedTab(assignedTab)
  }, [assignedTab])

  const { mutate: setCustomMenuKey, isPending: isSaving } = postWorkspacesApiSetWorkspaceCustomMenuKey.useMutation({
    path: { workspaceId },
  })

  const handleTabChange = (value: string) => {
    if (!value || value === selectedTab) {
      return
    }

    const previousTab = selectedTab
    const menuKey = value === DEFAULT_TAB_VALUE ? '' : value
    setSelectedTab(value)

    // openapi-qraft sends string bodies as raw text, but ASP.NET [FromBody] string expects JSON (e.g. "my-key").
    setCustomMenuKey(JSON.stringify(menuKey), {
      onSuccess: () => {
        getWorkspacesApiGetCurrentUserWorkspace.invalidateQueries({
          parameters: { path: { workspaceId } },
        })
        getWorkspacesApiGetCurrentUserWorkspaces.invalidateQueries()
        getLandingApiGetPlatformOperations.invalidateQueries({
          parameters: {
            query: {
              appId: applicationId,
              projectId: workspaceId,
              grouping: 'menu',
            },
          },
        })
        toast.success(value === DEFAULT_TAB_VALUE ? 'Custom menu cleared' : 'Custom menu assigned to workspace')
      },
      onError: () => {
        setSelectedTab(previousTab)
        toast.error('Failed to update custom menu')
      },
    })
  }

  return (
    <SectionContainer title="Custom Menu" description="Select a custom menu for this application">
      {customMenusPending ? (
        <LoadingComponent text="Loading custom menus..." />
      ) : customMenusError ? (
        <ErrorComponent />
      ) : menuOptions.length === 0 ? (
        <EmptyState
          title="No custom menus found"
          description="Create a custom menu for this application in the menu builder to assign it to the workspace."
          actions={
            <Link to={`/applications/${appKey}/menu`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
              Go to application menu builder
            </Link>
          }
        />
      ) : (
        <div className="flex items-end gap-5 font-light">
          <ToggleGroup
            aria-label="Select workspace custom menu"
            className="h-[35px] w-max rounded-md bg-background p-0.5 shadow"
            type="single"
            value={selectedTab}
            disabled={isSaving}
            onValueChange={handleTabChange}
          >
            <ToggleGroupItem className="h-[32px] px-2 text-xs font-medium" value={DEFAULT_TAB_VALUE}>
              Default
            </ToggleGroupItem>
            {menuOptions.map((menu) => (
              <ToggleGroupItem key={menu.key} className="h-[32px] px-2 text-xs font-medium" value={menu.key}>
                {menu.name}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <span>
            ...or{' '}
            <Link to={`/applications/${appKey}/menu`} className="underline underline-offset-2">
              create a new custom menu
            </Link>
          </span>
        </div>
      )}
    </SectionContainer>
  )
}
